import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCircle, X, ReceiptX, Info, Eye, Warning, HardHat, ChatCircleDots } from "@phosphor-icons/react";

const KIND_META = {
  chat_message: { color: "#8B5CF6", icon: ChatCircleDots },
  quote: { color: "#F59E0B", icon: ReceiptX },
  info: { color: "#005CFF", icon: Info },
  report_opened: { color: "#8B5CF6", icon: Eye },
  report_reminder: { color: "#EF4444", icon: Warning },
  chantier_created: { color: "#10B981", icon: HardHat },
  chantier_assigned: { color: "#005CFF", icon: HardHat },
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setData(r.data);
    } catch (err) {
      console.warn("Notifications fetch failed:", err?.message || err);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => clearInterval(timerRef.current);
  }, []);

  const readOne = async (n) => {
    if (!n.read) {
      await api.patch(`/notifications/${n.id}/read`);
    }
    setOpen(false);
    if (n.link) nav(n.link);
    load();
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); load(); }}
        className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 relative"
        data-testid="notif-bell"
        aria-label="Notifications"
      >
        <Bell size={18} weight={data.unread > 0 ? "fill" : "regular"} className={data.unread > 0 ? "text-[#F59E0B]" : ""} />
        {data.unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F59E0B] text-black text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[70] pointer-events-none">
          {/* Mobile / tablet: full backdrop. Desktop (lg+): only backdrop over the main content area, sidebar stays visible/interactive */}
          <div
            className="absolute inset-0 lg:left-64 bg-black/60 backdrop-blur-sm pointer-events-auto"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute top-16 right-3 left-3 sm:left-auto sm:w-[380px] lg:right-5 lg:top-5 max-h-[75vh] rounded-2xl bg-[#111] border border-white/10 overflow-hidden flex flex-col shadow-2xl pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="notif-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="font-display font-semibold">Notifications</div>
              <div className="flex items-center gap-2">
                {data.unread > 0 && (
                  <button onClick={readAll} className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white" data-testid="notif-read-all">Tout marquer lu</button>
                )}
                <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center"><X size={14} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {data.items.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle size={28} className="mx-auto text-emerald-400 mb-2" weight="duotone" />
                  <div className="text-sm text-white/60">Aucune notification</div>
                </div>
              ) : (
                data.items.map((n) => {
                  const meta = KIND_META[n.kind] || KIND_META.info;
                  return (
                    <button
                      key={n.id}
                      onClick={() => readOne(n)}
                      className={`w-full text-left px-4 py-3 flex gap-3 border-b border-white/5 hover:bg-white/[0.03] ${n.read ? "opacity-60" : ""}`}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${meta.color}22` }}>
                        <meta.icon size={16} weight="duotone" color={meta.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{n.title}</div>
                        {n.body && <div className="text-xs text-white/60 line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                          {new Date(n.created_at).toLocaleString("fr-FR")}
                        </div>
                      </div>
                      {!n.read && <div className="w-2 h-2 rounded-full bg-[#005CFF] mt-2 shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
